import React, { useState } from 'react';
import { X, Users, Building2, ShieldCheck, UserPlus, Trash2, Plus, Mail, Save, Eye, EyeOff, RotateCcw, Download, Upload, Check, Palette } from 'lucide-react';
import { useProjectStore } from '../store/useProjectStore';
import { generateId } from '../utils/vsmGeometry';
import { createOrganizationFromPreset } from '../data/defaultOrganization';
import type { OrgManagerProps, TeamMember, TabType, NewMember, NewDept, NewRole } from './OrgManager/types';
import { validateNewDept, validateNewRole, createDepartment, createRole, updateDepartmentHeadcount } from './OrgManager/validators';
import { importRolesFromText } from './OrgManager/importUtils';

export const OrgManager = ({ isOpen, onClose }: OrgManagerProps) => {
  const { organization, setOrganization } = useProjectStore();
  const colorCardsBySector = useProjectStore(s => s.colorCardsBySector);
  const setColorCardsBySector = useProjectStore(s => s.setColorCardsBySector);
  const sectorSuggestions = useProjectStore(s => s.sectorSuggestions);
  const removeSectorSuggestion = useProjectStore(s => s.removeSectorSuggestion);
  const promoteSectorSuggestion = useProjectStore(s => s.promoteSectorSuggestion);
  const [activeTab, setActiveTab] = useState<TabType>('team');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddDept, setShowAddDept] = useState(false);
  const [showAddRole, setShowAddRole] = useState<string | null>(null);
  const [newMember, setNewMember] = useState<NewMember>({ name: '', email: '', roleId: '', departmentId: '' });
  const [newDept, setNewDept] = useState<NewDept>({ name: '', color: '#3b82f6', shiftMinutes: 480, shiftsPerDay: 1 });
  const [newRole, setNewRole] = useState<NewRole>({ name: '', departmentId: '', costPerHour: 0, quantity: 1, shiftMinutes: 480, shiftsPerDay: 1 });
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editingDeptId, setEditingDeptId] = useState<string | null>(null);
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');

  if (!isOpen) return null;

  const handleAddMember = () => {
    if (!newMember.name || !newMember.email) return;
    setTeamMembers([...teamMembers, { ...newMember, id: generateId() }]);
    setNewMember({ name: '', email: '', roleId: '', departmentId: '' });
    setShowAddMember(false);
  };

  const handleAddDept = () => {
    const error = validateNewDept(newDept, organization);
    if (error) {
      alert(error);
      return;
    }
    
    const dept = createDepartment(newDept);
    setOrganization({
      ...organization,
      departments: [...organization.departments, dept]
    });
    setNewDept({ name: '', color: '#3b82f6', shiftMinutes: 480, shiftsPerDay: 1 });
    setShowAddDept(false);
  };

  const handleAddRole = () => {
    const error = validateNewRole(newRole, organization);
    if (error) {
      alert(error);
      return;
    }
    
    const role = createRole(newRole);
    
    // Atualizar totalHeadcount do departamento
    const updatedDepartments = updateDepartmentHeadcount(
      organization.departments,
      newRole.departmentId,
      role.quantity
    );
    
    const updatedOrg = {
      ...organization,
      departments: updatedDepartments,
      roles: [...organization.roles, role]
    };
    
    setOrganization(updatedOrg);
    setNewRole({ name: '', departmentId: '', costPerHour: 0, quantity: 1, shiftMinutes: 480, shiftsPerDay: 1 });
    setShowAddRole(null);
  };

  const handleDeleteMember = (id: string) => {
    const member = teamMembers.find(m => m.id === id);
    const name = member?.name || 'este membro';
    if (!window.confirm(`Remover "${name}" da equipe?`)) return;
    setTeamMembers(teamMembers.filter(m => m.id !== id));
  };

  const handleDeleteDept = (id: string) => {
    const dept = organization.departments.find(d => d.id === id);
    const name = dept?.name || 'este departamento';
    if (!window.confirm(`Excluir o departamento "${name}"? Todos os cargos vinculados serão removidos.`)) return;
    setOrganization({
      ...organization,
      departments: organization.departments.filter(d => d.id !== id),
      roles: organization.roles.filter(r => r.departmentId !== id)
    });
  };

  const handleDeleteRole = (id: string) => {
    const roleToDelete = organization.roles.find(r => r.id === id);
    if (!roleToDelete) return;
    
    if (!window.confirm(`Deseja realmente excluir o cargo "${roleToDelete.name}"?`)) {
      return;
    }
    
    // Atualizar totalHeadcount do departamento
    const updatedDepartments = updateDepartmentHeadcount(
      organization.departments,
      roleToDelete.departmentId,
      -(roleToDelete.quantity || 1)
    );
    
    setOrganization({
      ...organization,
      departments: updatedDepartments,
      roles: organization.roles.filter(r => r.id !== id)
    });
  };

  const handleToggleRoleVisibility = (id: string) => {
    const roleToUpdate = organization.roles.find(r => r.id === id);
    if (!roleToUpdate) return;
    
    // Tratar undefined como true (visível)
    const currentVisibility = roleToUpdate.visible !== false;
    
    setOrganization({
      ...organization,
      roles: organization.roles.map(r => 
        r.id === id ? { ...r, visible: !currentVisibility } : r
      )
    });
  };

  const handleToggleDeptVisibility = (deptId: string) => {
    const dept = organization.departments.find(d => d.id === deptId);
    if (!dept) return;
    
    // Se visible é undefined, trata como true (visível por padrão)
    const currentVisibility = dept.visible !== false;
    const newVisibility = !currentVisibility;
    
    setOrganization({
      ...organization,
      departments: organization.departments.map(d => 
        d.id === deptId ? { ...d, visible: newVisibility } : d
      )
    });
  };

  const handleResetToDefault = () => {
    if (window.confirm('Isso irá substituir toda a estrutura organizacional atual pelos dados pr-cadastrados. Confirma?')) {
      const defaultOrg = createOrganizationFromPreset();
      setOrganization(defaultOrg);
    }
  };

  const handleUpdateRole = (id: string, data: Partial<typeof organization.roles[0]>) => {
    const oldRole = organization.roles.find(r => r.id === id);
    if (!oldRole) return;
    
    const updatedRole = { ...oldRole, ...data };
    
    // Se a quantidade mudou, atualizar totalHeadcount
    let updatedDepartments = organization.departments;
    if (data.quantity !== undefined && data.quantity !== oldRole.quantity) {
      const diff = (data.quantity || 1) - (oldRole.quantity || 1);
      updatedDepartments = updateDepartmentHeadcount(updatedDepartments, oldRole.departmentId, diff);
    }
    
    // Se o departamento mudou, atualizar totalHeadcount de ambos
    if (data.departmentId !== undefined && data.departmentId !== oldRole.departmentId) {
      updatedDepartments = updateDepartmentHeadcount(updatedDepartments, oldRole.departmentId, -(oldRole.quantity || 1));
      updatedDepartments = updateDepartmentHeadcount(updatedDepartments, data.departmentId, updatedRole.quantity || 1);
    }
    
    setOrganization({
      ...organization,
      departments: updatedDepartments,
      roles: organization.roles.map(r => r.id === id ? updatedRole : r)
    });
  };

  const handleUpdateDept = (id: string, data: Partial<typeof organization.departments[0]>) => {
    setOrganization({
      ...organization,
      departments: organization.departments.map(d => d.id === id ? { ...d, ...data } : d)
    });
  };

  const handleImportFromText = () => {
    try {
      const result = importRolesFromText(importText, organization);
      setOrganization({ ...organization, departments: result.departments, roles: result.roles });
      setImportText('');
      setShowImportModal(false);
      alert(`Importao concluída! ${result.count} cargos adicionados.`);
    } catch (error) {
      alert('Erro ao importar. Use o formato: Setor[TAB]Cargo[TAB]Custo[TAB]Quantidade');
    }
  };

  //  CSV helpers 
  const downloadCSV = (filename: string, rows: string[][]) => {
    const bom = '\uFEFF';
    const csv = bom + rows.map(r =>
      r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim()); current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleDownloadTeamTemplate = () => {
    downloadCSV('template_colaboradores.csv', [
      ['Nome', 'Email', 'Setor', 'Cargo'],
      ['Joo Silva', 'joao@empresa.com', 'Produo', 'Operador'],
      ['Maria Santos', 'maria@empresa.com', 'Qualidade', 'Analista'],
    ]);
  };

  const handleUploadCollaborators = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.csv';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = (ev.target?.result as string).replace(/^\uFEFF/, '');
        const lines = text.trim().split(/\r?\n/).slice(1).filter(l => l.trim());
        const added: typeof teamMembers = [];
        lines.forEach(line => {
          const [name, email, sectorName, roleName] = parseCSVLine(line);
          if (!name || !email) return;
          const dept = organization.departments.find(d =>
            d.name.toLowerCase() === (sectorName || '').toLowerCase()
          );
          const role = organization.roles.find(r =>
            r.name.toLowerCase() === (roleName || '').toLowerCase()
          );
          added.push({ id: generateId(), name, email, departmentId: dept?.id || '', roleId: role?.id || '' });
        });
        if (added.length > 0) {
          setTeamMembers(prev => [...prev, ...added]);
          alert(`${added.length} colaborador(es) importado(s).`);
        } else {
          alert('Nenhum colaborador importado. Verifique o formato do arquivo.');
        }
      };
      reader.readAsText(file, 'UTF-8');
    };
    input.click();
  };

  const handleDownloadStructureTemplate = () => {
    downloadCSV('template_setores_cargos.csv', [
      ['Setor', 'Cor', 'MinPorTurno', 'TurnosPorDia', 'Cargo', 'CustoPorHora', 'Quantidade'],
      ['Produo', '#3b82f6', '480', '1', 'Operador', '25.00', '10'],
      ['Produo', '#3b82f6', '480', '1', 'Supervisor', '40.00', '2'],
      ['Qualidade', '#22c55e', '480', '2', 'Analista de Qualidade', '35.00', '3'],
    ]);
  };

  const handleUploadStructure = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.csv';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = (ev.target?.result as string).replace(/^\uFEFF/, '');
        const lines = text.trim().split(/\r?\n/).slice(1).filter(l => l.trim());
        let updatedDepts = [...organization.departments];
        const newRoles: any[] = [];
        lines.forEach(line => {
          const [sectorName, color, minStr, turnsStr, roleName, costStr, qtyStr] = parseCSVLine(line);
          if (!sectorName || !roleName) return;
          let dept = updatedDepts.find(d => d.name.toLowerCase() === sectorName.toLowerCase());
          if (!dept) {
            dept = { id: generateId(), name: sectorName, color: color || '#3b82f6',
              totalHeadcount: 0, visible: true,
              shiftMinutes: parseInt(minStr) || 480, shiftsPerDay: parseInt(turnsStr) || 1 };
            updatedDepts = [...updatedDepts, dept];
          }
          const qty = Math.max(1, parseInt(qtyStr) || 1);
          updatedDepts = updatedDepts.map(d =>
            d.id === dept!.id ? { ...d, totalHeadcount: (d.totalHeadcount || 0) + qty } : d
          );
          newRoles.push({ id: generateId(), name: roleName, departmentId: dept.id,
            costPerHour: parseFloat(costStr) || 0, currency: 'BRL', quantity: qty,
            shiftMinutes: parseInt(minStr) || 480, shiftsPerDay: parseInt(turnsStr) || 1, visible: true });
        });
        if (newRoles.length > 0) {
          setOrganization({ ...organization, departments: updatedDepts, roles: [...organization.roles, ...newRoles] });
          alert(`${newRoles.length} cargo(s) / setor(es) importado(s).`);
        } else {
          alert('Nenhum registro importado. Verifique o formato do arquivo.');
        }
      };
      reader.readAsText(file, 'UTF-8');
    };
    input.click();
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="rounded shadow-xl w-full max-w-5xl h-[88vh] flex flex-col overflow-hidden" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: '0 20px 60px rgba(2,6,23,0.28)', color: 'var(--text-primary)' }}>
        
        {/* Header - Estilo Ribbon */}
        <div className="p-4 flex justify-between items-center" style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-default)' }}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-200 dark:bg-slate-600 rounded transition-colors">
              <Users size={18} className="text-gray-700 dark:text-white" />
            </div>
            <div>
              <h2 className="font-bold text-sm text-gray-800 dark:text-slate-100">Gesto Organizacional</h2>
              <p className="text-[10px] text-gray-500 dark:text-slate-400 uppercase font-medium">Time, Estrutura e KPIs do Projeto</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={handleResetToDefault}
              className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1.5"
              title="Restaurar estrutura pr-cadastrada (10 setores, 44 cargos)"
            >
              <RotateCcw size={14} />
              Resetar para Padro
            </button>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded transition-colors text-gray-600 dark:text-slate-400"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Tabs - Estilo Ribbon */}
        <div className="flex overflow-x-auto" style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-default)' }}>
          {[
            { id: 'team', label: 'Colaboradores', icon: Users },
            { id: 'dept', label: 'Setores e Cargos', icon: Building2 },
            { id: 'suggestions', label: 'Sugestões', icon: ShieldCheck },

          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button 
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-6 py-3 text-[11px] font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-t-2 border-t-blue-600 text-gray-800 dark:text-slate-100' 
                    : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700'
                }`}
              style={{ background: activeTab === tab.id ? 'var(--bg-surface)' : undefined }}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden min-h-0">
          {activeTab === 'team' && (
            <div className="h-full overflow-y-auto p-6 space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                  <Users size={16} className="text-gray-500 dark:text-slate-400" />
                  Colaboradores
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDownloadTeamTemplate}
                    className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-300 rounded text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1.5"
                    title="Baixar template Excel/CSV"
                  >
                    <Download size={13} /> Template
                  </button>
                  <button
                    onClick={handleUploadCollaborators}
                    className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-300 rounded text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1.5"
                    title="Carregar CSV preenchido"
                  >
                    <Upload size={13} /> Carregar
                  </button>
                  <button
                    onClick={() => setShowAddMember(true)}
                    className="px-3 py-1.5 bg-gray-200 dark:bg-slate-600 text-gray-800 dark:text-white rounded text-xs font-medium hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors flex items-center gap-1.5"
                  >
                    <UserPlus size={13} /> Adicionar
                  </button>
                </div>
              </div>

              {showAddMember && (
                <div className="p-4 rounded mb-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)' }}>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <input
                      type="text"
                      placeholder="Nome"
                      value={newMember.name}
                      onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                      className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-100"
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                      className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-100"
                    />
                    <select
                      value={newMember.roleId}
                      onChange={(e) => setNewMember({ ...newMember, roleId: e.target.value })}
                      className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-100"
                    >
                      <option value="">Selecione o Cargo</option>
                      {organization.roles.map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                    <select
                      value={newMember.departmentId}
                      onChange={(e) => setNewMember({ ...newMember, departmentId: e.target.value })}
                      className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-100"
                    >
                      <option value="">Selecione o Setor</option>
                      {organization.departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={handleAddMember}
                      className="px-4 py-2 bg-gray-200 dark:bg-slate-600 text-gray-800 dark:text-white rounded text-xs font-medium hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors"
                    >
                      Salvar
                    </button>
                    <button 
                      onClick={() => { setShowAddMember(false); setNewMember({ name: '', email: '', roleId: '', departmentId: '' }); }}
                      className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              <div className="overflow-hidden rounded" style={{ border: '1px solid var(--border-default)' }}>
                <table className="w-full text-left">
                  <thead style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-default)' }}>
                    <tr>
                      <th className="px-4 py-3 text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase">Nome</th>
                      <th className="px-4 py-3 text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase">Email</th>
                      <th className="px-4 py-3 text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase">Cargo</th>
                      <th className="px-4 py-3 text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase">Setor</th>
                      <th className="px-4 py-3 text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase text-right">Aes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
                    {teamMembers.map(member => (
                      <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                        <td className="px-4 py-3 text-xs font-medium text-gray-800 dark:text-slate-100">{member.name}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-slate-400 flex items-center gap-1">
                          <Mail size={12} className="text-gray-400 dark:text-slate-500" />
                          {member.email}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-slate-400">
                          {organization.roles.find(r => r.id === member.roleId)?.name || '-'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-slate-400">
                          {organization.departments.find(d => d.id === member.departmentId)?.name || '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button 
                            onClick={() => handleDeleteMember(member.id)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {teamMembers.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-xs text-gray-400 dark:text-slate-500">
                          Nenhum membro cadastrado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'suggestions' && (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="max-w-xl space-y-2">
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                  Setores criados temporariamente durante o mapeamento. Promova para oficializar e exibir na gestão organizacional.
                </p>
                {sectorSuggestions.length === 0 ? (
                  <div className="text-center py-10 text-xs text-gray-400 dark:text-slate-500">
                    Nenhuma sugestão de setor pendente
                  </div>
                ) : (
                  sectorSuggestions.map(name => (
                    <div key={name} className="flex items-center justify-between px-3 py-2.5 border border-dashed border-amber-300 rounded-md bg-amber-50 dark:bg-amber-900/10">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-bold text-amber-500 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">tmp</span>
                        <span className="text-xs font-medium text-gray-800 dark:text-slate-100">{name}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => promoteSectorSuggestion(name)}
                          title="Promover a setor oficial"
                          className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
                        >
                          <ShieldCheck size={12} />
                          Promover
                        </button>
                        <button
                          onClick={() => removeSectorSuggestion(name)}
                          title="Descartar sugestão"
                          className="p-1 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors rounded"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'dept' && (
            <div className="flex gap-0 h-full min-h-0">

              {/*  PAINEL ESQUERDO: Lista de Setores  */}
              <div className="w-64 flex-shrink-0 flex flex-col" style={{ background: 'var(--bg-elevated)', borderRight: '1px solid var(--border-default)' }}>
                {/* Toolbar do painel esquerdo */}
                  <div className="flex items-center justify-between px-3 py-2" style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)' }}>
                  <span className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Setores</span>
                  <div className="flex items-center gap-1">
                    <button onClick={handleDownloadStructureTemplate} className="p-1.5 text-gray-400 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-100 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors" title="Baixar template CSV"><Download size={13} /></button>
                    <button onClick={handleUploadStructure} className="p-1.5 text-gray-400 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-100 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors" title="Carregar CSV"><Upload size={13} /></button>
                    <button
                      onClick={() => setColorCardsBySector(!colorCardsBySector)}
                      title={colorCardsBySector ? 'Desativar colorao por setor' : 'Colorir cards pelo setor do recurso'}
                      className={`p-1.5 rounded transition-colors ${
                        colorCardsBySector
                          ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                          : 'text-gray-400 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-100 hover:bg-gray-100 dark:hover:bg-slate-700'
                      }`}
                    ><Palette size={13} /></button>
                    <button onClick={() => setShowAddDept(v => !v)} className="p-1.5 text-gray-400 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-100 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors" title="Adicionar setor"><Plus size={13} /></button>
                  </div>
                </div>

                {/* Formulrio novo setor */}
                {showAddDept && (
                  <div className="p-3 space-y-2" style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)' }}>
                    <div className="flex gap-2">
                      <input type="color" value={newDept.color} onChange={(e) => setNewDept({ ...newDept, color: e.target.value })} className="w-8 h-7 border border-gray-300 dark:border-slate-600 rounded cursor-pointer flex-shrink-0" />
                      <input type="text" placeholder="Nome do Setor" value={newDept.name} onChange={(e) => setNewDept({ ...newDept, name: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && handleAddDept()} className="flex-1 px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100" autoFocus />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-0.5">min/turno</label>
                        <input type="number" min="60" max="1440" step="30" value={newDept.shiftMinutes} onChange={(e) => setNewDept({ ...newDept, shiftMinutes: Math.max(60, parseInt(e.target.value) || 480) })} className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100" />
                      </div>
                      <div className="flex-1">
                        <label className="block text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-0.5">Turnos/dia</label>
                        <select value={newDept.shiftsPerDay} onChange={(e) => setNewDept({ ...newDept, shiftsPerDay: parseInt(e.target.value) })} className="w-full px-2 py-1 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100">
                          <option value={1}>1T</option><option value={2}>2T</option><option value={3}>3T</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={handleAddDept} className="flex-1 py-1 bg-slate-700 dark:bg-slate-600 text-white rounded text-xs font-medium hover:bg-slate-800 dark:hover:bg-slate-500 transition-colors">Salvar</button>
                      <button onClick={() => { setShowAddDept(false); setNewDept({ name: '', color: '#3b82f6', shiftMinutes: 480, shiftsPerDay: 1 }); }} className="flex-1 py-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-300 rounded text-xs hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">Cancelar</button>
                    </div>
                  </div>
                )}

                {/* Lista de setores */}
                <div className="flex-1 overflow-y-auto">
                  {organization.departments.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs text-gray-400 dark:text-slate-500">Nenhum setor cadastrado</div>
                  ) : (
                    organization.departments.map(dept => {
                      const isSelected = selectedDeptId === dept.id;
                      const deptRoles = organization.roles.filter(r => r.departmentId === dept.id);
                      const isVisible = dept.visible !== false;
                      return (
                        <div key={dept.id}
                          onClick={() => setSelectedDeptId(dept.id)}
                          className={`group flex items-center gap-2 px-3 py-2 cursor-pointer border-b border-gray-100 dark:border-slate-700 transition-colors ${
                            isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500' : 'hover:bg-white dark:hover:bg-slate-700 border-l-2 border-l-transparent'
                          } ${!isVisible ? 'opacity-40' : ''}`}
                        >
                          {/* Swatch de cor */}
                          <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: dept.color }} />

                          {/* Nome */}
                          {editingDeptId === dept.id ? (
                            <input
                              type="text" value={dept.name}
                              onChange={(e) => handleUpdateDept(dept.id, { name: e.target.value })}
                              onBlur={() => setEditingDeptId(null)}
                              onKeyDown={(e) => { if (e.key === 'Enter') setEditingDeptId(null); e.stopPropagation(); }}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 min-w-0 px-1.5 py-0.5 border border-blue-400 rounded text-xs"
                              autoFocus
                            />
                          ) : (
                            <span className="flex-1 min-w-0 text-xs text-gray-700 dark:text-slate-200 truncate font-medium">{dept.name}</span>
                          )}

                          {/* Badges */}
                          <span className="text-[9px] text-gray-400 dark:text-slate-500 flex-shrink-0">{deptRoles.length}</span>

                          {/* Aes (visveis no hover ou quando selecionado) */}
                          <div className={`flex items-center gap-0.5 flex-shrink-0 ${
                            isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                          } transition-opacity`}>
                            <button onClick={(e) => { e.stopPropagation(); setEditingDeptId(editingDeptId === dept.id ? null : dept.id); }} className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors" title="Renomear">
                              <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleToggleDeptVisibility(dept.id); }} className="p-1 text-gray-400 hover:text-gray-700 rounded transition-colors" title={isVisible ? 'Ocultar' : 'Mostrar'}>
                              {isVisible ? <Eye size={10} /> : <EyeOff size={10} />}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteDept(dept.id); if (isSelected) setSelectedDeptId(null); }} className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors" title="Excluir">
                              <Trash2 size={10} />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/*  PAINEL DIREITO: Cargos do Setor Selecionado  */}
              <div className="flex-1 flex flex-col min-w-0" style={{ background: 'var(--bg-surface)' }}>
                {!selectedDeptId ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <Building2 size={32} className="text-gray-200 dark:text-slate-700 mx-auto mb-3" />
                      <p className="text-sm text-gray-400 dark:text-slate-500">Selecione um setor para ver os cargos</p>
                    </div>
                  </div>
                ) : (() => {
                  const dept = organization.departments.find(d => d.id === selectedDeptId)!;
                  if (!dept) return null;
                  const deptRoles = organization.roles.filter(r => r.departmentId === selectedDeptId);
                  const deptShift = (dept as any).shiftMinutes || 480;
                  const deptTurns = (dept as any).shiftsPerDay || 1;
                  return (
                    <>
                      {/* Header do painel direito */}
                      <div className="flex items-center justify-between px-4 py-2 flex-shrink-0" style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)' }}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: dept.color }} />
                          <span className="text-sm font-semibold text-gray-800 dark:text-slate-100">{dept.name}</span>
                          <span className="text-[10px] text-gray-400 dark:text-slate-500 ml-1">{deptShift/60}h{deptTurns}T (padro)</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Editar jornada do setor */}
                          {editingDeptId === `shift-${dept.id}` ? (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1">
                                <label className="text-[9px] text-gray-400">min</label>
                                <input type="number" min="60" max="1440" step="30" value={deptShift}
                                  onChange={(e) => handleUpdateDept(dept.id, { shiftMinutes: Math.max(60, parseInt(e.target.value) || 480) } as any)}
                                  className="w-14 px-1.5 py-1 border border-blue-300 rounded text-xs" />
                              </div>
                              <div className="flex items-center gap-1">
                                <label className="text-[9px] text-gray-400">T/dia</label>
                                <select value={deptTurns} onChange={(e) => handleUpdateDept(dept.id, { shiftsPerDay: parseInt(e.target.value) } as any)} className="w-10 px-1 py-1 border border-blue-300 rounded text-xs">
                                  <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
                                </select>
                              </div>
                              <button onClick={() => setEditingDeptId(null)} className="p-1 text-green-600 hover:text-green-700"><Check size={13} /></button>
                            </div>
                          ) : (
                            <button onClick={() => setEditingDeptId(`shift-${dept.id}`)} className="px-2 py-1 text-[10px] text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors" title="Editar jornada padro">Editar jornada</button>
                          )}
                          <button
                            onClick={() => { setShowAddRole(dept.id); setNewRole({ name: '', departmentId: dept.id, costPerHour: 0, quantity: 1, shiftMinutes: deptShift, shiftsPerDay: deptTurns }); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-700 dark:bg-slate-600 text-white rounded text-xs font-medium hover:bg-slate-800 dark:hover:bg-slate-500 transition-colors"
                          >
                            <Plus size={12} /> Cargo
                          </button>
                        </div>
                      </div>

                      {/* Formulrio adicionar cargo */}
                      {showAddRole === dept.id && (
                        <div className="px-4 py-3 flex-shrink-0" style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-default)' }}>
                          <div className="flex gap-2 items-end">
                            <div className="flex-1">
                              <label className="block text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1">Cargo</label>
                              <input type="text" placeholder="Nome do cargo" value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && handleAddRole()} className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-100" autoFocus />
                            </div>
                            <div className="w-16">
                              <label className="block text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1">R$/h</label>
                              <input type="number" value={newRole.costPerHour} onChange={(e) => setNewRole({ ...newRole, costPerHour: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-100" />
                            </div>
                            <div className="w-14">
                              <label className="block text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1">Qtd</label>
                              <input type="number" min="1" value={newRole.quantity} onChange={(e) => setNewRole({ ...newRole, quantity: parseInt(e.target.value) || 1 })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-100" />
                            </div>
                            <div className="w-14">
                              <label className="block text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1">min/T</label>
                              <input type="number" min="60" max="1440" step="30" value={newRole.shiftMinutes} onChange={(e) => setNewRole({ ...newRole, shiftMinutes: Math.max(60, parseInt(e.target.value) || 480) })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-100" />
                            </div>
                            <div className="w-12">
                              <label className="block text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1">T/dia</label>
                              <select value={newRole.shiftsPerDay} onChange={(e) => setNewRole({ ...newRole, shiftsPerDay: parseInt(e.target.value) })} className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-100">
                                <option value={1}>1T</option><option value={2}>2T</option><option value={3}>3T</option>
                              </select>
                            </div>
                            <button onClick={handleAddRole} className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors">Salvar</button>
                            <button onClick={() => { setShowAddRole(null); setNewRole({ name: '', departmentId: '', costPerHour: 0, quantity: 1, shiftMinutes: 480, shiftsPerDay: 1 }); }} className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-300 rounded text-xs hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">Cancelar</button>
                          </div>
                        </div>
                      )}

                      {/* Tabela de cargos */}
                      <div className="flex-1 overflow-y-auto">
                        {deptRoles.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-32 text-gray-300 dark:text-slate-600">
                            <ShieldCheck size={24} className="mb-2" />
                            <p className="text-xs">Nenhum cargo neste setor</p>
                          </div>
                        ) : (
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-default)' }}>
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wide">Cargo</th>
                                <th className="px-3 py-2 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wide w-16 text-center">Qtd</th>
                                <th className="px-3 py-2 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wide w-24">R$/hora</th>
                                <th className="px-3 py-2 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wide w-20 text-center">Jornada</th>
                                <th className="px-3 py-2 w-20"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {deptRoles.map((role, idx) => (
                                <tr key={role.id}
                                  className={`border-b border-gray-100 dark:border-slate-800 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors ${
                                  role.visible === false ? 'opacity-40' : ''
                                } ${idx % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-slate-800/30'}`}>
                                  {/* Cargo */}
                                  <td className="px-4 py-2">
                                    {editingRoleId === role.id ? (
                                      <input type="text" value={role.name} onChange={(e) => handleUpdateRole(role.id, { name: e.target.value })} onKeyDown={(e) => e.key === 'Escape' && setEditingRoleId(null)} className="w-full px-2 py-0.5 border border-blue-400 rounded text-xs" autoFocus />
                                    ) : (
                                      <span className="text-xs text-gray-800 dark:text-slate-100 font-medium">{role.name}</span>
                                    )}
                                  </td>
                                  {/* Qtd */}
                                  <td className="px-3 py-2 text-center">
                                    {editingRoleId === role.id ? (
                                      <input type="number" min="1" value={role.quantity || 1} onChange={(e) => handleUpdateRole(role.id, { quantity: parseInt(e.target.value) || 1 })} className="w-14 px-1.5 py-0.5 border border-blue-400 rounded text-xs text-center" />
                                    ) : (
                                      <span className="text-xs text-gray-600 dark:text-slate-400">{role.quantity || 1}</span>
                                    )}
                                  </td>
                                  {/* Custo */}
                                  <td className="px-3 py-2">
                                    {editingRoleId === role.id ? (
                                      <input type="number" step="0.01" value={role.costPerHour} onChange={(e) => handleUpdateRole(role.id, { costPerHour: parseFloat(e.target.value) || 0 })} className="w-20 px-1.5 py-0.5 border border-blue-400 rounded text-xs" />
                                    ) : (
                                      <span className="text-xs text-gray-600 dark:text-slate-400">R$ {role.costPerHour.toFixed(2)}</span>
                                    )}
                                  </td>
                                  {/* Jornada */}
                                  <td className="px-3 py-2 text-center">
                                    {editingRoleId === role.id ? (
                                      <div className="flex gap-1 justify-center">
                                        <input type="number" min="60" max="1440" step="30" title="min/turno" value={(role as any).shiftMinutes || 480} onChange={(e) => handleUpdateRole(role.id, { shiftMinutes: Math.max(60, parseInt(e.target.value) || 480) } as any)} className="w-14 px-1 py-0.5 border border-blue-400 rounded text-xs" />
                                        <select title="Turnos/dia" value={(role as any).shiftsPerDay || 1} onChange={(e) => handleUpdateRole(role.id, { shiftsPerDay: parseInt(e.target.value) } as any)} className="w-10 px-0.5 py-0.5 border border-blue-400 rounded text-xs">
                                          <option value={1}>1T</option><option value={2}>2T</option><option value={3}>3T</option>
                                        </select>
                                      </div>
                                    ) : (
                                      <span className="text-[10px] text-gray-400 dark:text-slate-500">{((role as any).shiftMinutes || 480)/60}h{(role as any).shiftsPerDay || 1}T</span>
                                    )}
                                  </td>
                                  {/* Aes */}
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-1 justify-end">
                                      {editingRoleId === role.id ? (
                                        <button onClick={() => setEditingRoleId(null)} className="p-1 text-green-600 hover:text-green-700" title="Confirmar"><Check size={13} /></button>
                                      ) : (
                                        <button onClick={() => setEditingRoleId(role.id)} className="p-1 text-gray-400 hover:text-blue-600 rounded" title="Editar">
                                          <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                      )}
                                      <button onClick={() => handleToggleRoleVisibility(role.id)} className="p-1 text-gray-400 hover:text-gray-600 rounded" title={role.visible === false ? 'Mostrar' : 'Ocultar'}>
                                        {role.visible === false ? <EyeOff size={12} /> : <Eye size={12} />}
                                      </button>
                                      <button onClick={() => handleDeleteRole(role.id)} className="p-1 text-gray-400 hover:text-red-500 rounded" title="Excluir">
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>

                      {/* Status bar */}
                      <div className="flex items-center gap-4 px-4 py-1.5 flex-shrink-0" style={{ background: 'var(--bg-elevated)', borderTop: '1px solid var(--border-default)' }}>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500">{deptRoles.length} {deptRoles.length === 1 ? 'cargo' : 'cargos'}</span>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500">{dept.totalHeadcount || 0} {dept.totalHeadcount === 1 ? 'colaborador' : 'colaboradores'}</span>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500">Jornada padro: {deptShift/60}h  {deptTurns} {deptTurns === 1 ? 'turno' : 'turnos'}/dia</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}





        </div>

        {/* Footer - Estilo Ribbon */}
        <div className="p-4 flex justify-end gap-2" style={{ background: 'var(--bg-elevated)', borderTop: '1px solid var(--border-default)' }}>
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 dark:bg-slate-500 text-gray-800 dark:text-white rounded text-xs font-medium hover:bg-gray-300 dark:hover:bg-slate-400 transition-colors flex items-center gap-2"
          >
            <Save size={14} />
            Salvar e Fechar
          </button>
        </div>
      </div>

      {/* Modal de Importação */}
      {showImportModal && (
        <div className="fixed inset-0 z-[10001] bg-black/50 flex items-center justify-center p-4">
          <div className="rounded shadow-xl w-full max-w-2xl" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}>
            <div className="p-4 flex justify-between items-center" style={{ borderBottom: '1px solid var(--border-default)' }}>
              <h3 className="font-bold text-sm text-gray-800 dark:text-slate-100">Importar Cargos</h3>
              <button onClick={() => setShowImportModal(false)} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-xs text-blue-800 font-medium mb-2">Formato de Importação:</p>
                <p className="text-xs text-blue-700 mb-1">
                  Cole dados do Excel ou texto com TAB entre colunas:
                </p>
                <code className="text-xs bg-white px-2 py-1 rounded block text-gray-700">
                  Setor[TAB]Cargo[TAB]Custo[TAB]Quantidade
                </code>
                <p className="text-xs text-blue-600 mt-2">
                  Exemplo: Produção[TAB]Operador[TAB]25[TAB]10
                </p>
              </div>
              <textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Cole aqui os dados do Excel...&#10;Produção	Operador	25	10&#10;Qualidade	Inspetor	32	3"
                className="w-full h-48 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded text-xs font-mono bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100"
              />
            </div>
              <div className="p-4 flex justify-end gap-2" style={{ borderTop: '1px solid var(--border-default)' }}>
              <button 
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                Cancelar
              </button>
              <button 
                onClick={handleImportFromText}
                className="px-4 py-2 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700"
              >
                Importar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Export default para compatibilidade total
export default OrgManager;

